import type { Action, Computed, Thunk } from 'easy-peasy';
import { action, computed, thunk } from 'easy-peasy';
import type { StoreModel } from '..';
import * as utils from './utils';
import type { Injections } from '../services/injections';
import type { ApplicationErrorOptions } from '../../types';
import type {
  CarrierSelection,
  SampleCarrierSelection,
  SampleCarrierSelectionCriteria,
  SampleCarrierSelectionSummary,
  CarrierSelectionShippingAggregates,
  DeliveryEstimateRequest,
  DeliveryEstimateResponse,
  SubscriptionTimingRequest,
  SubscriptionTimingResponse,
} from '../services/ShiptimeService/types';
import type { RateCalculationTestRecord } from '../services/CarrierService/types';
import { adaptShipmentToCarrierSelectionResponse } from './ShipmentAdapter';
import parseStatusCode from '../errors/parseStatusCode';
import { shapeCancelHierarchy } from './utils';
import type {
  ShiptimeCarrierLabel,
  ShiptimeLtlCostResponse,
  ShiptimeMegaShipmentProcessCreate,
  ShiptimeShipmentParameters,
  ShiptimeShipmentResponse,
} from '@shipium-corp/shipium-ts-types';
import { trackLabelPrint } from '~/utils/analytics/userActionAnalytics';

export interface CancelShipmentPayload {
  shipiumShipmentId: string;
  carrierSelectionId?: string;
  shipiumLabelId?: string;
}

export interface FetchCarrierSelectionPayload {
  shipmentId: string;
  carrierSelectionId: string;
}
export const PRINT_LABEL_TAG_PREFIX = 'generate-label-ui';
export const PACK_APP_PRINT_LABEL_TAG_PREFIX = 'generate-pack-label-ui';
export interface ShipmentsModel {
  sampleCarrierSelectionsSummary: Record<string, SampleCarrierSelectionSummary>;
  sampleCarrierSelections: Record<string, SampleCarrierSelection>;
  shipmentActionFailure: Action<ShipmentsModel, ApplicationErrorOptions>;
  shipmentActionStart: Action<ShipmentsModel, void>;
  lastAction?: number;
  fetchTimestamps: Record<string, number>;
  choroplethHoverState: Record<string, any>;
  sampleCarrierSelectionCriteria: SampleCarrierSelectionCriteria;
  setChoroplethHoverState: Action<ShipmentsModel, Record<string, any>>;
  setSampleCarrierSelectionCriteria: Action<ShipmentsModel, SampleCarrierSelectionCriteria>;
  setSampleCarrierSelectionSummary: Action<ShipmentsModel, Record<string, SampleCarrierSelectionSummary>>;
  defaultSampleCarrierSelectionKey: Computed<ShipmentsModel, string>;
  carrierSelectionShippingAggregates?: CarrierSelectionShippingAggregates;
  shipments: Record<string, ShiptimeShipmentResponse>;
  ltlShipments: Record<string, ShiptimeLtlCostResponse>;
  carrierLabels: Record<string, ShiptimeCarrierLabel>;
  carrierSelections: Record<string, CarrierSelection>;
  addShipmentToCache: Action<ShipmentsModel, ShiptimeShipmentResponse>;
  addShipmentToPrintLabelShipmentCache: Action<ShipmentsModel, ShiptimeShipmentResponse>;
  printLabelShipments: Record<string, ShiptimeShipmentResponse>;
  removeShipmentFromCache: Action<ShipmentsModel, string>;
  addLtlShipmentToCache: Action<ShipmentsModel, ShiptimeLtlCostResponse>;
  addCarrierLabelToCache: Action<ShipmentsModel, ShiptimeCarrierLabel>;
  addCarrierSelectionToCache: Action<ShipmentsModel, CarrierSelection>;
  addSampleCarrierSelectionToCache: Action<ShipmentsModel, SampleCarrierSelection>;
  clearShipmentCache: Action<ShipmentsModel, void>;
  fetchShipmentById: Thunk<
    ShipmentsModel,
    string,
    Injections,
    StoreModel,
    Promise<ShiptimeShipmentResponse | undefined>
  >;
  fetchShipmentByLabelId: Thunk<
    ShipmentsModel,
    string,
    Injections,
    StoreModel,
    Promise<ShiptimeShipmentResponse | undefined>
  >;
  fetchCarrierSelectionById: Thunk<
    ShipmentsModel,
    FetchCarrierSelectionPayload,
    Injections,
    StoreModel,
    Promise<CarrierSelection>
  >;
  fetchLtlCostById: Thunk<ShipmentsModel, string, Injections, StoreModel, Promise<ShiptimeLtlCostResponse | undefined>>;
  createLabel: Thunk<
    ShipmentsModel,
    ShiptimeMegaShipmentProcessCreate,
    Injections,
    StoreModel,
    Promise<ShiptimeShipmentResponse>
  >;
  shipmentToCarrierSelectionResponse: Computed<
    ShipmentsModel,
    (shipmentId: string) => RateCalculationTestRecord | undefined
  >;
  cancelShipment: Thunk<
    ShipmentsModel,
    CancelShipmentPayload,
    Injections,
    StoreModel,
    Promise<ShiptimeShipmentResponse>
  >;
  labelRequest?: ShiptimeMegaShipmentProcessCreate;
  setActiveCarrierLabelId: Action<ShipmentsModel, string | undefined>;
  setLabelRequest: Action<ShipmentsModel, Partial<ShiptimeMegaShipmentProcessCreate>>;
  activeCarrierLabelId?: string;
  printLabelFormMetadata?: Record<string, any>;
  setPrintLabelFormMetadata: Action<ShipmentsModel, object>;
  fetchDeliveryEstimate: Thunk<
    ShipmentsModel,
    DeliveryEstimateRequest,
    Injections,
    StoreModel,
    Promise<DeliveryEstimateResponse>
  >;
  fetchSubscriptionTiming: Thunk<
    ShipmentsModel,
    SubscriptionTimingRequest,
    Injections,
    StoreModel,
    Promise<SubscriptionTimingResponse>
  >;
  fetchDeliveryEstimateById: Thunk<ShipmentsModel, string, Injections, StoreModel, Promise<DeliveryEstimateResponse>>;
  fetchSubscriptionTimingById: Thunk<
    ShipmentsModel,
    string,
    Injections,
    StoreModel,
    Promise<SubscriptionTimingResponse>
  >;
  setDeliveryEstimate: Action<ShipmentsModel, DeliveryEstimateResponse>;
  deliveryEstimates: Record<string, DeliveryEstimateResponse>;
  subscriptionTimingResponses: Record<string, SubscriptionTimingResponse>;
  setSubscriptionTiming: Action<ShipmentsModel, SubscriptionTimingResponse>;
  fetchGeneratedLabelZPL: Thunk<ShipmentsModel, string, Injections, StoreModel, Promise<string>>;
  setGeneratedLabelZPL: Action<ShipmentsModel, { zpl: string; labelId: string }>;
  zplCache: Record<string, string>;
}

export const shipmentsModel: ShipmentsModel = {
  shipments: {},
  printLabelShipments: {},
  ltlShipments: {},
  zplCache: {},
  choroplethHoverState: {},
  fetchTimestamps: {},
  sampleCarrierSelectionCriteria: {},
  sampleCarrierSelectionsSummary: {},
  deliveryEstimates: {},
  subscriptionTimingResponses: {},
  carrierLabels: {},
  carrierSelections: {},
  sampleCarrierSelections: {},
  printLabelFormMetadata: {},
  defaultSampleCarrierSelectionKey: computed((state) => {
    const defaultKey =
      Object.values(state.sampleCarrierSelectionsSummary)
        .sort((a, b) => {
          return a.fetchedTimestamp - b.fetchedTimestamp;
        })
        .pop()?.cacheKey || '';
    return defaultKey;
  }),

  setPrintLabelFormMetadata: action((state, payload) => {
    state.printLabelFormMetadata = { ...state.printLabelFormMetadata, ...payload };
  }),
  setChoroplethHoverState: action((state, payload) => {
    state.choroplethHoverState = { ...state.choroplethHoverState, ...payload };
  }),
  shipmentActionStart: action((state, payload) => {
    state.lastAction = Date.now();
  }),
  shipmentActionFailure: action((state, payload) => {
    // Log status and message only; the raw error's config.data is the full request body. warn, not error:
    // RUM records console.error as an application error, and most of these are expected 4xx.
    console.warn('shipment action failed', { httpCode: payload.httpCode, message: payload.message });
  }),
  addShipmentToCache: action((state, payload) => {
    if (payload.shipiumShipmentId) {
      state.shipments[payload.shipiumShipmentId] = payload;
    }
  }),
  addShipmentToPrintLabelShipmentCache: action((state, payload) => {
    if (payload.shipiumShipmentId) {
      state.printLabelShipments[payload.shipiumShipmentId] = payload;
    }
  }),
  removeShipmentFromCache: action((state, payload) => {
    const { [payload]: shipmentId, ...rest } = state.shipments;
    state.shipments = rest;
  }),
  addLtlShipmentToCache: action((state, payload) => {
    state.ltlShipments[payload.shipiumLtlCostId] = payload;
  }),
  addCarrierLabelToCache: action((state, payload) => {
    if (payload?.shipiumLabelId) {
      state.carrierLabels[payload.shipiumLabelId] = payload;
    }
  }),
  addCarrierSelectionToCache: action((state, payload) => {
    // index by shipmentId instead of carrierSelectionId since they are 1:1
    state.carrierSelections[payload.shipiumShipmentId] = payload;
  }),
  addSampleCarrierSelectionToCache: action((state, payload) => {
    state.sampleCarrierSelections[payload.cacheKey] = payload;
  }),
  setSampleCarrierSelectionSummary: action((state, payload) => {
    state.fetchTimestamps.sampleCarrierSelectionsSummary = Date.now();
    state.sampleCarrierSelectionsSummary = payload;
  }),
  clearShipmentCache: action((state) => {
    state.shipments = {};
  }),
  setLabelRequest: action((state, payload) => {
    const timestamp = new Date().toISOString();
    const customLabelEntries = payload.labelParameters?.customLabelEntries
      ? payload.labelParameters?.customLabelEntries
      : state.labelRequest?.labelParameters?.customLabelEntries;
    const labelParameters = { ...state.labelRequest?.labelParameters, ...payload.labelParameters, customLabelEntries };

    const shipmentParameters = { ...state.labelRequest?.shipmentParameters, ...payload?.shipmentParameters };

    // Get existing tags from state
    const existingTags = state.labelRequest?.shipmentParameters?.shipmentTags || [];
    // Find if there's already an auto-generated tag
    const existingAutoTag = existingTags.find((tag) => tag.startsWith(PRINT_LABEL_TAG_PREFIX));
    // Get new user tags (filter out any auto-generated tags)
    const newUserTags = (shipmentParameters.shipmentTags || []).filter(
      (tag) => !tag.startsWith(PRINT_LABEL_TAG_PREFIX),
    );
    // Build final tags: keep existing auto-tag OR create new one, then add user tags
    const finalShipmentTags = existingAutoTag
      ? [existingAutoTag, ...newUserTags]
      : [`${PRINT_LABEL_TAG_PREFIX}-${timestamp}`, ...newUserTags];

    const packagingType = {
      ...state.labelRequest?.shipmentParameters?.packagingType,
      ...payload.shipmentParameters?.packagingType,
    };
    const shipFromAddress = {
      ...state.labelRequest?.shipmentParameters?.shipFromAddress,
      ...payload.shipmentParameters?.shipFromAddress,
    };
    const destinationAddress = {
      ...state.labelRequest?.shipmentParameters?.destinationAddress,
      ...payload.shipmentParameters?.destinationAddress,
    };
    const orderItemQuantities =
      (payload.shipmentParameters?.orderItemQuantities || []) ??
      (state.labelRequest?.shipmentParameters?.orderItemQuantities || []);
    const totalWeight = {
      ...state.labelRequest?.shipmentParameters?.totalWeight,
      ...payload.shipmentParameters?.totalWeight,
    };
    const desiredDeliveryDateOptions = {
      ...state.labelRequest?.shipmentParameters?.desiredDeliveryDateOptions,
      ...payload.shipmentParameters?.desiredDeliveryDateOptions,
    };
    const totalDeclaredValue = {
      ...state.labelRequest?.shipmentParameters?.totalDeclaredValue,
      ...payload.shipmentParameters?.totalDeclaredValue,
    };

    const currencyCode = payload?.currencyCode || state.labelRequest?.currencyCode || 'USD';

    const hasShipmentParameters = Object.keys(shipmentParameters).length > 0;
    const hasDesiredDeliveryDateOptions = Object.keys(desiredDeliveryDateOptions).length > 0;
    const hasTotalDeclaredValue = Object.keys(totalDeclaredValue).length > 0;

    state.labelRequest = {
      ...state.labelRequest,
      ...payload,
      currencyCode,
      shipmentParameters: hasShipmentParameters
        ? ({
            ...shipmentParameters,
            currencyCode,
            packagingType,
            shipFromAddress,
            destinationAddress,
            shippedDateTime: new Date().toISOString(),
            orderedDateTime: new Date().toISOString(),
            shipmentTags: finalShipmentTags,
            orderItemQuantities,
            totalWeight,
            ...(hasDesiredDeliveryDateOptions ? { ...desiredDeliveryDateOptions, currencyCode } : {}),
            ...(hasTotalDeclaredValue ? { ...totalDeclaredValue, currencyCode } : {}),
          } as ShiptimeShipmentParameters)
        : undefined,
      labelParameters: {
        ...labelParameters,
        currencyCode: currencyCode,
      },
    };
  }),
  setActiveCarrierLabelId: action((state, payload) => {
    state.activeCarrierLabelId = payload;
  }),
  setSampleCarrierSelectionCriteria: action((state, payload) => {
    state.sampleCarrierSelectionCriteria = { ...state.sampleCarrierSelectionCriteria, ...payload };
  }),
  fetchShipmentById: thunk(async (actions, payload, { injections, getState, getStoreState }) => {
    try {
      const { shipments } = getState();
      const { session } = getStoreState();
      const partnerId = session.user?.sessionPartnerId;
      if (shipments[payload]) {
        return shipments[payload];
      }
      actions.shipmentActionStart();
      const res = await injections.shiptimeService.getShipmentByShipmentId(payload, partnerId);
      actions.addShipmentToCache(res);
      if (res.carrierSelection?.carrierSelectionId) {
        await actions.fetchCarrierSelectionById({
          shipmentId: payload,
          carrierSelectionId: res.carrierSelection.carrierSelectionId,
        });
      }
      return res;
    } catch (error) {
      actions.shipmentActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
    }
  }),
  fetchShipmentByLabelId: thunk(async (actions, payload, { injections }) => {
    try {
      actions.shipmentActionStart();
      const labelRes = await injections.shiptimeService.getLabelByLabelId(payload);

      if (!labelRes?.shipiumShipmentId) {
        throw new Error(`Unable to locate a shipment for labelId: ${payload}`);
      }

      const res = await injections.shiptimeService.getShipmentByShipmentId(labelRes.shipiumShipmentId);
      actions.addShipmentToCache(res);
      if (res.carrierSelection?.carrierSelectionId) {
        await actions.fetchCarrierSelectionById({
          shipmentId: labelRes.shipiumShipmentId!,
          carrierSelectionId: res.carrierSelection.carrierSelectionId,
        });
      }
      return res;
    } catch (error) {
      actions.shipmentActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
    }
  }),
  fetchLtlCostById: thunk(async (actions, payload, { injections }) => {
    try {
      actions.shipmentActionStart();
      const res = await injections.shiptimeService.fetchLtlCostById(payload);
      actions.addLtlShipmentToCache(res);
      return res;
    } catch (error) {
      actions.shipmentActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
    }
  }),
  fetchCarrierSelectionById: thunk(async (actions, payload, { injections, getStoreState }) => {
    try {
      const { session } = getStoreState();
      const partnerId = session.user?.sessionPartnerId;
      actions.shipmentActionStart();
      const res = await injections.shiptimeService.getShipmentCarrierSelectionById(
        payload.shipmentId,
        payload.carrierSelectionId,
        partnerId,
      );
      actions.addCarrierSelectionToCache(utils.adaptV2CarrierSelectionResponseToV1(res));
      return res;
    } catch (error) {
      actions.shipmentActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),
  cancelShipment: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      actions.shipmentActionStart();
      const res = await injections.shiptimeService.cancelShipment({
        ...shapeCancelHierarchy(payload),
        cascadeCancel: true,
      });
      actions.removeShipmentFromCache(res.shipiumShipmentId);
      const shipRes = await actions.fetchShipmentById(res.shipiumShipmentId);
      if (shipRes?.carrierLabel) {
        actions.addCarrierLabelToCache(shipRes?.carrierLabel);
      }
      return shipRes as ShiptimeShipmentResponse;
    } catch (error) {
      actions.shipmentActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      throw error;
    }
  }),
  // remery 2-24-2025
  // take note that this createLabel thunk now adds shipments to specific
  // printLabelShipments cache, this is to avoid an issue described in CUS-7941
  // where cached data from the print tool was making the shipment detail page
  // not include patched in admin only carrier selection details
  // You should probably just never use this :/
  createLabel: thunk(async (actions, payload, { injections }) => {
    try {
      actions.shipmentActionStart();
      const response = await injections.shiptimeService.createLabel(payload);
      if (response.carrierLabel) {
        actions.addCarrierLabelToCache(response.carrierLabel);
        actions.setActiveCarrierLabelId(response.carrierLabel.shipiumLabelId);
      }
      actions.addShipmentToPrintLabelShipmentCache(response);
      trackLabelPrint({
        success: true,
        shipmentId: response.shipiumShipmentId,
      });
      return response;
    } catch (error) {
      const httpCode = parseStatusCode(error);
      actions.shipmentActionFailure({
        error,
        httpCode,
        message: error.message,
        asNotification: true,
      });
      trackLabelPrint({
        success: false,
        error: { httpCode, message: error?.message },
      });
      return error;
    }
  }),
  // Temporary to reuse existing carrier rate response component
  shipmentToCarrierSelectionResponse: computed((state) => (shipmentId) => {
    const shipment = state.shipments[shipmentId];
    return shipment && !!shipment?.destinationAddress ? adaptShipmentToCarrierSelectionResponse(shipment) : undefined;
  }),
  setDeliveryEstimate: action((state, payload) => {
    state.deliveryEstimates[payload.deliveryEstimateId] = payload;
  }),
  setSubscriptionTiming: action((state, payload) => {
    state.subscriptionTimingResponses[payload.subscriptionTimingRequestId] = payload;
  }),
  fetchDeliveryEstimateById: thunk(async (actions, payload, { injections }) => {
    try {
      actions.shipmentActionStart();
      const res = await injections.shiptimeService.getDeliveryEstimate(payload);
      actions.setDeliveryEstimate(res);
      return res;
    } catch (error) {
      actions.shipmentActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),
  fetchSubscriptionTimingById: thunk(async (actions, payload, { injections }) => {
    try {
      actions.shipmentActionStart();
      const res = await injections.shiptimeService.getSubscriptionTiming(payload);
      actions.setSubscriptionTiming(res);
      return res;
    } catch (error) {
      actions.shipmentActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),
  fetchDeliveryEstimate: thunk(async (actions, payload, { injections }) => {
    try {
      actions.shipmentActionStart();
      const res = await injections.shiptimeService.createDeliveryEstimate(payload);
      actions.setDeliveryEstimate(res);
      return res;
    } catch (error) {
      actions.shipmentActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),
  fetchSubscriptionTiming: thunk(async (actions, payload, { injections }) => {
    try {
      actions.shipmentActionStart();
      const res = await injections.shiptimeService.createSubscriptionTiming(payload);
      actions.setSubscriptionTiming(res);
      return res;
    } catch (error) {
      actions.shipmentActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),
  // get the zpl string for an existing shipium label
  fetchGeneratedLabelZPL: thunk(async (actions, payload, { injections }) => {
    try {
      actions.shipmentActionStart();
      const zpl = await injections.shiptimeService.fetchLabelZPL(payload);
      actions.setGeneratedLabelZPL({ labelId: payload, zpl });
      return zpl;
    } catch (error) {
      actions.shipmentActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      throw error;
    }
  }),
  setGeneratedLabelZPL: action((state, payload) => {
    state.zplCache[payload.labelId] = payload.zpl;
  }),
};
